import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, clearToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = authed, false = not authed
  const [settings, setSettings] = useState(null);
  const [subscription, setSubscription] = useState(undefined); // undefined=loading, null=none, string=tier
  const [activeKid, setActiveKidState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bubz_active_kid") || "null"); } catch { return null; }
  });

  const refreshSubscription = useCallback(async () => {
    try { const { data } = await api.get("/subscription"); setSubscription(data.subscription); return data.subscription; }
    catch { setSubscription(null); return null; }
  }, []);

  const setActiveKid = useCallback((kid) => {
    setActiveKidState(kid);
    if (kid) localStorage.setItem("bubz_active_kid", JSON.stringify(kid));
    else localStorage.removeItem("bubz_active_kid");
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      return data;
    } catch { return null; }
  }, []);

  const refreshKid = useCallback(async () => {
    const stored = JSON.parse(localStorage.getItem("bubz_active_kid") || "null");
    if (!stored || stored.parent) return null;
    try {
      const { data } = await api.get(`/profiles/${stored.profile_id}`);
      setActiveKid({ ...data });
      return data;
    } catch { return null; }
  }, [setActiveKid]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadSettings();
    } catch {
      clearToken();
      setUser(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user && user !== false) { refreshSubscription(); loadSettings(); }
  }, [user, refreshSubscription, loadSettings]);

  const value = {
    user, setUser, checkAuth, activeKid, setActiveKid,
    settings, loadSettings, refreshKid,
    subscription, refreshSubscription,
    isKid: !!activeKid && !activeKid.parent,
    logout: async () => {
      try { await api.post("/auth/logout"); } catch (e) { console.error("logout request failed", e); }
      clearToken();
      setActiveKid(null);
      setSubscription(undefined);
      setUser(false);
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
